import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFacilityDashboard, useRecipe } from "../lib/api/queries";
import { isFacilityDashboardEnabled } from "../lib/capabilities";
import { useVisibleNavItems } from "../lib/nav";
import { normalizeSearch, storeSubtitle, storeTitle } from "../lib/util";
import { Icon } from "./icons";

type Props = { open: boolean; onClose: () => void };

type CmdkItem = {
  id: string;
  kind: "ai" | "page" | "tienda";
  label: string;
  shortcut?: string;
  icon: keyof typeof Icon;
  onSelect: () => void;
};

export function CommandPalette({ open, onClose }: Props) {
  const [q, setQ] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const navigate = useNavigate();
  const { data: recipe } = useRecipe();
  const { data } = useFacilityDashboard(isFacilityDashboardEnabled(recipe));
  // Same resolved list the Sidebar renders, so ⌘K never offers a screen
  // this org or role does not have (it used to read the raw NAV_ITEMS).
  const navItems = useVisibleNavItems();

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setSelectedIndex(-1);
    }
  }, [open]);

  // Reset selection when query changes.
  useEffect(() => {
    setSelectedIndex(-1);
  }, [q]);

  const lowerQ = q.toLowerCase();

  const items = useMemo<CmdkItem[]>(() => {
    const out: CmdkItem[] = [];
    if (q) {
      out.push({
        id: "ai-ask",
        kind: "ai",
        label: `"${q}"`,
        shortcut: "↵",
        icon: "Sparkles",
        onSelect: () => go("/ia"),
      });
    }
    const pageMatches = navItems
      .filter((n) => !q || n.label.toLowerCase().includes(lowerQ))
      .slice(0, 6);
    for (const n of pageMatches) {
      out.push({
        id: `page-${n.key}`,
        kind: "page",
        label: n.label,
        icon: n.icon,
        onSelect: () => go(n.path),
      });
    }
    const tiendaMatches = (data?.byAsset ?? [])
      .filter((t) => {
        if (!q) return true;
        // Same identity rule and accent folding as the Tiendas list, so
        // the palette finds and names a store the way every other
        // surface does (unit first, address as the secondary line).
        const haystack = normalizeSearch([t.unit, t.address].filter(Boolean).join(" "));
        return haystack.includes(normalizeSearch(q));
      })
      .slice(0, 6);
    for (const t of tiendaMatches) {
      const subtitle = storeSubtitle(t);
      out.push({
        id: `tienda-${t.id}`,
        kind: "tienda",
        label: storeTitle(t, "(sin dirección)"),
        ...(subtitle ? { shortcut: subtitle } : {}),
        icon: "Store",
        onSelect: () => go(`/tiendas/${t.id}`),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, data, lowerQ, navItems]);

  function go(path: string) {
    navigate(path);
    onClose();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setSelectedIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setSelectedIndex(items.length - 1);
    } else if (e.key === "Enter") {
      const idx = selectedIndex >= 0 ? selectedIndex : 0;
      const item = items[idx];
      if (item) {
        e.preventDefault();
        item.onSelect();
      }
    }
  }

  // Scroll active item into view.
  useEffect(() => {
    if (selectedIndex < 0) return;
    const node = itemRefs.current[selectedIndex];
    if (node) node.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (!open) return null;

  const aiItem = items.find((i) => i.kind === "ai");
  const pageItems = items.filter((i) => i.kind === "page");
  const tiendaItems = items.filter((i) => i.kind === "tienda");
  const activeId = selectedIndex >= 0 ? items[selectedIndex]?.id : undefined;

  function renderItem(item: CmdkItem) {
    const globalIdx = items.indexOf(item);
    const IconC = Icon[item.icon];
    const active = globalIdx === selectedIndex;
    return (
      <button
        key={item.id}
        id={item.id}
        type="button"
        role="option"
        aria-selected={active}
        className={`cmdk__item${active ? " cmdk__item--active" : ""}`}
        onClick={item.onSelect}
        onMouseEnter={() => setSelectedIndex(globalIdx)}
        ref={(el) => {
          itemRefs.current[globalIdx] = el;
        }}
      >
        <IconC s={16} />
        <span>{item.label}</span>
        {item.shortcut && <span className="cmdk__item-shortcut">{item.shortcut}</span>}
      </button>
    );
  }

  return (
    <div className="cmdk-backdrop" onClick={onClose} role="presentation">
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Búsqueda rápida"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="cmdk__input-wrap">
          <Icon.Search />
          <input
            ref={inputRef}
            className="cmdk__input"
            placeholder="Tipea para buscar, ir a una pantalla o preguntar a la IA…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls="cmdk-listbox"
            aria-autocomplete="list"
            {...(activeId ? { "aria-activedescendant": activeId } : {})}
          />
          <span className="cmdk__kbd">esc</span>
        </div>
        <div
          className="cmdk__list"
          id="cmdk-listbox"
          role="listbox"
          aria-label="Resultados de búsqueda"
        >
          {aiItem && (
            <>
              <div className="cmdk__section-label">Preguntar a la IA</div>
              {renderItem(aiItem)}
            </>
          )}

          <div className="cmdk__section-label">Ir a</div>
          {pageItems.map(renderItem)}

          {tiendaItems.length > 0 && (
            <>
              <div className="cmdk__section-label">Tiendas</div>
              {tiendaItems.map(renderItem)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
